<script lang="ts">
  // The duel's card-motion layer: a single fixed, screen-space overlay that flies REAL card elements
  // (and art-slice debris shards) along the true source→destination path of every board event. It is
  // the one owner of card TRAVEL in BOTH renderers — it resolves each zone's on-screen rect from the
  // flat board's `data-mref` tags (2D) or the WebGL arena's world→screen projector (3D), so a card
  // sent to the graveyard, drawn off the deck, bounced to the hand, or shattered in battle animates
  // identically whether the field underneath is HTML or WebGL. The arena keeps only its in-place
  // accents (flash / shake / lunge); everything that moves across the board moves here.
  //
  // Built on GSAP timelines (MotionPath arcs + the house CustomEase curves) — transform/opacity
  // only, GPU-composited, one global timeScale knob. Honours prefers-reduced-motion (cards simply
  // update in place). The 2D board's contract is SMOOTH + INTENT-ACCURATE, never cinematic — the
  // cinema budget belongs to the 3D arena.
  import { onMount } from "svelte";
  import { onMotion, arenaZoneRect, arenaActive, mrefKey, setRefHidden, setHandHidden, duel, type ScreenRect, type DuelMotion } from "../../stores/duel.svelte.ts";
  import { LOCATION, POS } from "@ygoevo/protocol";
  import { cardImage } from "../../../data.ts";
  import { sleeveById, DEFAULT_SLEEVE } from "../../duel/sleeves.ts";
  import { ATTACK_TIMING, DUR, FX } from "../../duel/motion.ts";
  import { settings } from "../../stores/settings.svelte.ts";
  import { gsap } from "gsap";
  import { MotionPathPlugin } from "gsap/MotionPathPlugin";
  import { CustomEase } from "gsap/CustomEase";

  gsap.registerPlugin(MotionPathPlugin, CustomEase);
  // The house curves, verbatim from lib/duel/motion.ts's CSS beziers, so a GSAP flight moves to the
  // exact beat the rest of the duel does. Every curve DECELERATES and never overshoots — no
  // back/elastic/bounce anywhere (DESIGN.md: "Motion = data", it flows, it doesn't boing).
  const OUT_QUINT = CustomEase.create("dfOutQuint", "0.22,1,0.36,1");
  const OUT_EXPO = CustomEase.create("dfOutExpo", "0.16,1,0.3,1");
  const IN_QUINT = CustomEase.create("dfInQuint", "0.64,0,0.78,0");

  const backImg = sleeveById(DEFAULT_SLEEVE).img ?? "";
  const FIELD = LOCATION.MZONE | LOCATION.SZONE | LOCATION.FZONE;

  let host: HTMLDivElement;
  const reduced = () => typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;

  // One global "how fast do duels play" knob (Settings → duel speed). Every flight/pulse/shuffle here
  // parents to GSAP's global timeline, so a single timeScale keeps them all in step — including the
  // stagger delays. Reset on unmount so the knob never bleeds into non-duel screens.
  $effect(() => {
    gsap.globalTimeline.timeScale(settings.duelSpeed || 1);
    return () => gsap.globalTimeline.timeScale(1);
  });

  // ── interruption safety ─────────────────────────────────────────────────────────
  // Every animation this layer creates is tracked so component teardown can kill the lot — a kill
  // fires each animation's own onInterrupt, which is where flight copies remove themselves and real
  // board cards restore identity. Without this, leaving a duel mid-flight strands overlay elements.
  const live = new Set<gsap.core.Animation>();
  function track<T extends gsap.core.Animation>(a: T): T {
    live.add(a);
    const prevC = a.eventCallback("onComplete");
    const prevI = a.eventCallback("onInterrupt");
    a.eventCallback("onComplete", () => { live.delete(a); prevC?.(); });
    a.eventCallback("onInterrupt", () => { live.delete(a); prevI?.(); });
    return a;
  }

  type Ref = { controller: number; location: number; sequence: number };

  /** A zone's on-screen rect: the flat board's tagged element first, else the 3D arena's projection. */
  function rectFor(r: Ref): { rect: ScreenRect; dom: boolean } | null {
    const el = host?.ownerDocument.querySelector<HTMLElement>(`[data-mref="${mrefKey(r.controller, r.location, r.sequence)}"]`);
    if (el) { const b = el.getBoundingClientRect(); if (b.width) return { rect: b, dom: true }; }
    const proj = arenaZoneRect(r.controller, r.location, r.sequence);
    return proj ? { rect: proj, dom: false } : null;
  }

  /** The tweenable INNER card of a board ref. The `[data-mref]` wrapper carries an inline transform
   *  (the playmat-alignment vars), so in-place beats (lunge, shuffle, pulse) must NEVER touch it —
   *  they tween the transform-free `.tp-card` inside instead, and always return it to identity.
   *  Piles have no `.tp-card` (callers fall back to rect-anchored FX); hand refs resolve by index. */
  function innerCardEl(r: Ref): HTMLElement | null {
    if (r.location & LOCATION.HAND) {
      const row = host?.ownerDocument.querySelector<HTMLElement>(`[data-mref-hand="${r.controller === duel.seat ? "me" : "opp"}"]`);
      const child = row?.children[r.sequence] as HTMLElement | undefined;
      return child?.querySelector<HTMLElement>(".tp-card") ?? null;
    }
    return host?.ownerDocument.querySelector<HTMLElement>(`[data-mref="${mrefKey(r.controller, r.location, r.sequence)}"] .tp-card`) ?? null;
  }

  // One live timeline per REAL board element: a new beat kills the old one FIRST (its onInterrupt
  // restores identity), so consecutive attacks/shuffles never compound transforms. The element's own
  // Tailwind transition-transform would chase GSAP a frame behind (mushy trails), so it's suspended
  // for the tween's lifetime and always restored — restoring it BEFORE clearing the transform lets
  // the card's native 150ms transition smooth any interrupted-mid-pose snap back to identity.
  const boardTweens = new WeakMap<HTMLElement, gsap.core.Timeline>();
  function boardTween(el: HTMLElement, build: (tl: gsap.core.Timeline) => void, delayMs = 0): void {
    boardTweens.get(el)?.kill();
    const prevTransition = el.style.transition;
    el.style.transition = "none";
    let done = false;
    const restore = () => {
      if (done) return;
      done = true;
      boardTweens.delete(el);
      el.style.transition = prevTransition;
      gsap.set(el, { clearProps: "transform" });
    };
    const tl = gsap.timeline({ delay: delayMs / 1000, onComplete: restore, onInterrupt: restore });
    build(tl);
    boardTweens.set(el, track(tl));
  }

  /** A SINGLE card-sized rect at the hand (not the whole row). Prefers a real hand card's rect; else
   *  derives one from the row height. Fixes the "giant sleeve" — a card leaving the hand was being
   *  sized to the entire hand container. */
  function handCardRect(controller: number): ScreenRect | null {
    const side = controller === duel.seat ? "me" : "opp";
    const row = host?.ownerDocument.querySelector<HTMLElement>(`[data-mref-hand="${side}"]`);
    if (!row) return null;
    const cards = [...row.children].map((c) => c.getBoundingClientRect()).filter((r) => r.width);
    if (cards.length) return cards[cards.length - 1]!; // the card just played sat at an end
    const b = row.getBoundingClientRect();
    if (!b.width) return null;
    const h = b.height, w = h * (59 / 86);
    return { left: b.left + b.width / 2 - w / 2, top: b.top, width: w, height: h };
  }

  /** Where a card ACTUALLY leaves the hand: the real hand element (yours found by passcode, the
   *  opponent's masked hand lifts its end card), hidden for the flight so it never double-shows.
   *  The hide key lives in `duel.hidden`, which the next snapshot (where the card is gone) clears. */
  function handSource(controller: number, code: number): { rect: ScreenRect; hide: () => void } | null {
    const side = controller === duel.seat ? "me" : "opp";
    const row = host?.ownerDocument.querySelector<HTMLElement>(`[data-mref-hand="${side}"]`);
    const hand = duel.board?.players[controller]?.hand ?? [];
    let idx = side === "me" && code ? hand.findIndex((c) => c.code === code) : hand.length - 1;
    if (idx < 0) idx = hand.length - 1;
    const el = row?.children[idx] as HTMLElement | undefined;
    const rect = el?.getBoundingClientRect();
    if (!rect?.width) {
      const fb = handCardRect(controller);
      return fb ? { rect: fb, hide: () => {} } : null;
    }
    const hk = `hand:${side}:${idx}`;
    return {
      rect,
      hide: () => { setRefHidden(hk, true); setTimeout(() => setRefHidden(hk, false), 1500); }, // snapshot clears first
    };
  }

  // ── flight-copy retirement, synchronized to the board ─────────────────────────────
  // A copy that lands ON the board (a zone / the hand) must cover its spot until the REAL card exists
  // beneath it — the next snapshot — and vanish that same paint. That handoff is what makes a play read
  // as ONE continuous card instead of "fades out, then reappears". `done` unhides the real card first;
  // the copy is removed a frame later so the two always overlap, never gap.
  let boardGen = 0;
  type Landed = { el: HTMLElement; done: () => void };
  let awaitingBoard: Landed[] = [];
  function retire(it: Landed): void {
    it.done();
    // a TIMER, never requestAnimationFrame: rAF starvation (the observed freeze mode) would strand
    // the copy on screen forever — the "faded white card that just stays" bug. Timers survive it.
    setTimeout(() => it.el.remove(), 50);
  }
  function retireOnBoard(el: HTMLElement, genAtLaunch: number, done: () => void = () => {}): void {
    const it = { el, done };
    if (boardGen > genAtLaunch) { retire(it); return; } // a snapshot already landed mid-flight
    awaitingBoard.push(it);
    setTimeout(() => { // safety: a negated play may never produce the snapshot this waits for
      const i = awaitingBoard.indexOf(it);
      if (i >= 0) { awaitingBoard.splice(i, 1); retire(it); }
    }, 1200);
  }
  $effect(() => {
    void duel.board;
    boardGen++;
    const landed = awaitingBoard;
    awaitingBoard = [];
    for (const it of landed) retire(it);
  });

  const cx = (r: ScreenRect) => r.left + r.width / 2;
  const cy = (r: ScreenRect) => r.top + r.height / 2;
  const artFor = (code: number, faceDown: boolean) => (faceDown || !code ? backImg : cardImage(code));

  /** A positioned card-sized element placed over a source rect: an outer wrapper (owned by the PATH
   *  timeline — position/scale/rotation/shadow) around an inner FACE (owned by the flip timeline),
   *  so a card can turn over while it flies, like a real card in the air. */
  function makeCard(rect: ScreenRect, img: string): { el: HTMLDivElement; face: HTMLDivElement } {
    const el = document.createElement("div");
    el.className = "mfly-card";
    el.style.cssText = `left:${rect.left}px;top:${rect.top}px;width:${rect.width}px;height:${rect.height}px`;
    const face = document.createElement("div");
    face.className = "mfly-cardface";
    if (img) face.style.backgroundImage = `url("${img}")`;
    el.appendChild(face);
    host.appendChild(el);
    return { el, face };
  }

  /** Turn the card over: the face rotates edge-on, the art swaps, and it rotates back — one real
   *  flip. A timeline on the INNER face only, so it composes with the wrapper's flight (independent
   *  transforms, no fighting; the wrapper's CSS perspective projects the turn). */
  function faceFlip(face: HTMLDivElement, toImg: string, delay: number, dur = 240): void {
    const half = dur / 2000;
    const tl = gsap.timeline({ delay: delay / 1000 });
    tl.to(face, { rotationY: 90, duration: half, ease: IN_QUINT })
      .add(() => { face.style.backgroundImage = toImg ? `url("${toImg}")` : ""; })
      .set(face, { rotationY: -90 })
      .to(face, { rotationY: 0, duration: half, ease: OUT_QUINT });
    track(tl);
  }

  // The anticipation beat before every flight: the card lifts 6px + swells before launching, so a
  // flight reads as the card being PICKED UP AND THROWN, not slid. flyPath's arc starts from this
  // pose, and faceFlip's mid-air delays offset by it so flips still land mid-arc.
  const ANT_MS = 80;

  /** THE 2D flight — one GSAP timeline with the full storyboard: anticipation hop → a real curved
   *  MotionPath arc (rotation resolving with a slight lean into the travel direction; scale bulging
   *  mid-air as the card is "picked off the table") → a decelerating landing settle. No overshoot
   *  anywhere (house law). Transform/opacity only (the shadow is static CSS), so it stays composited
   *  and never freezes mid-arc when a board apply hits the main thread. Resolves on landing; REJECTS
   *  when killed mid-flight (teardown), so callers' fail paths clean up their copies + hides. */
  function flyPath(el: HTMLElement, o: {
    dx: number; dy: number; scEnd: number; rotEnd: number; lift: number; dur: number;
    delay?: number; landOpacity?: number; fadeIn?: boolean; scStart?: number;
  }): Promise<void> {
    return new Promise((resolve, reject) => {
      const tilt = Math.max(-9, Math.min(9, o.dx * 0.02)); // lean into the direction of travel
      const sc0 = o.scStart ?? 1;
      const midScale = ((sc0 + o.scEnd) / 2) * 1.12;       // picked UP off the table mid-arc
      const ANT = ANT_MS / 1000, FLY = o.dur / 1000;
      const tl = gsap.timeline({ delay: (o.delay ?? 0) / 1000, onComplete: () => resolve(), onInterrupt: () => reject(new Error("flight interrupted")) });
      tl.to(el, { y: -6, scale: sc0 * 1.04, duration: ANT, ease: OUT_QUINT }, 0);
      // the arc: a genuine curve through a lifted midpoint (starting at the anticipation's -6px pose
      // so the path continues the hop instead of snapping back to 0 first). power2.out lands CRISP —
      // quint/expo have a long flat tail that made the card visibly crawl the last few px ("end lag").
      tl.to(el, {
        motionPath: { path: [{ x: 0, y: -6 }, { x: o.dx * 0.5, y: o.dy * 0.5 - o.lift }, { x: o.dx, y: o.dy }], curviness: 1.2 },
        duration: FLY, ease: "power2.out",
      }, ANT);
      // rotation + scale ride the same clock as the arc, landing EXACTLY on the final pose in one leg —
      // no appended settle tween (that trailing 120ms micro-scale after the card had visibly arrived
      // WAS the "slight lag before it reaches final position/orientation" the card looked done but kept
      // animating).
      tl.to(el, {
        keyframes: {
          "55%": { rotation: o.rotEnd * 0.5 + tilt, scale: midScale },
          "100%": { rotation: o.rotEnd, scale: o.scEnd },
          easeEach: "none",
        },
        duration: FLY, ease: "power2.out",
      }, ANT);
      if (o.fadeIn) tl.fromTo(el, { opacity: 0 }, { opacity: 1, duration: ANT + FLY * 0.5, ease: "none" }, 0);
      if ((o.landOpacity ?? 1) !== 1) tl.to(el, { opacity: o.landOpacity, duration: FLY * 0.45, ease: "none" }, ANT + FLY * 0.55);
      track(tl);
    });
  }

  /** Which side of the card is SHOWING when it lifts off (the board's truth at launch): a masked or
   *  set card shows its back; a face-up field/GY card its face; deck/extra stacks are backs. */
  function startsFaceDown(m: Extract<DuelMotion, { kind: "travel" }>, fromHand: boolean): boolean {
    if (m.revealAtHand || m.code === 0) return true;
    if (fromHand) return m.from.controller !== duel.seat; // their hand is hidden; yours shows faces
    if (m.from.location & FIELD) {
      const p = duel.board?.players[m.from.controller];
      const arr = (m.from.location & LOCATION.MZONE) ? p?.mzone : p?.szone;
      return (((arr?.[m.from.sequence]?.position ?? 0) & POS.FACEDOWN)) !== 0;
    }
    return (m.from.location & (LOCATION.DECK | LOCATION.EXTRA)) !== 0;
  }

  // ── travel: a card glides intact from one place to another (a play onto the field, send-to-GY,
  //    banish, discard, mill, bounce, return-to-deck). Field/hand landings arrive in the card's FINAL
  //    orientation and hold until the real card is dressed beneath them (retireOnBoard) — one
  //    continuous card, never a fade-out/reappear. Pile landings dim into the stack. ────────────────
  function travel(m: Extract<DuelMotion, { kind: "travel" }>): void {
    const gen = boardGen;
    const fromHand = (m.from.location & LOCATION.HAND) !== 0;
    const src = fromHand ? handSource(m.from.controller, m.code) : null;
    const fromRect = src?.rect ?? (fromHand ? null : (rectFor(m.from)?.rect ?? null));
    if (!fromRect) return;
    const toHand = (m.to.location & LOCATION.HAND) !== 0;
    const toField = (m.to.location & FIELD) !== 0;
    hideFieldSource(m.from);
    src?.hide();
    // launch showing what the board showed; if the landing side differs, the card TURNS OVER —
    // a revealed hand play flips IN THE HAND first, everything else flips mid-arc.
    const startDown = startsFaceDown(m, fromHand);
    const flips = startDown !== m.faceDown && (m.faceDown || m.code !== 0);
    const { el, face } = makeCard(fromRect, artFor(m.code, startDown));
    const pathDelay = m.revealAtHand && flips ? 210 : 0;
    if (flips) faceFlip(face, artFor(m.code, m.faceDown), m.revealAtHand ? 0 : ANT_MS + DUR.travel * 0.22);
    const fly = (to: { x: number; y: number; h: number }, endRot: number, landOpacity: number, onLand: () => void, fail: () => void) => {
      const dx = to.x - cx(fromRect), dy = to.y - cy(fromRect);
      const sc = Math.max(0.32, Math.min(1.6, to.h / fromRect.height));
      const lift = Math.min(70, Math.hypot(dx, dy) * 0.16) + 8; // a weighty arc, not a straight slide
      flyPath(el, { dx, dy, scEnd: sc, rotEnd: endRot, lift, dur: DUR.travel, delay: pathDelay, landOpacity })
        .then(onLand, fail);
    };
    if (toHand) {
      // bounce into the hand: land exactly on the end slot the card will occupy; the real card mounts
      // hidden beneath and takes over the moment the snapshot lands.
      const side = m.to.controller === duel.seat ? "me" : "opp";
      const n0 = duel.board?.players[m.to.controller]?.hand.length ?? 0;
      const slot = handSlot(m.to.controller, 0, 1);
      if (!slot) { el.remove(); return; }
      const hk = `hand:${side}:${n0}`;
      setHandHidden(hk, true);
      fly(slot, 0, 1,
        () => retireOnBoard(el, gen, () => setHandHidden(hk, false)),
        () => { el.remove(); setHandHidden(hk, false); });
      return;
    }
    const to = rectFor(m.to)?.rect ?? null;
    if (!to) { el.remove(); return; }
    if (toField) {
      // land in the card's FINAL rendered orientation — the opponent's cards read from their side
      // (180°), a defense-position monster lies sideways — so the dressed card underneath matches the
      // copy exactly and the handoff is invisible. The DESTINATION zone is hidden for the whole
      // flight: a snapshot that applies mid-air must not paint the real card under the flying copy
      // (that double-render was the "two images / it fades in early" bug).
      const endRot = (m.to.controller !== duel.seat ? 180 : 0) + ((m.to.location & LOCATION.MZONE) !== 0 && m.defense ? -90 : 0);
      const destKey = mrefKey(m.to.controller, m.to.location, m.to.sequence);
      setHandHidden(destKey, true);
      fly({ x: cx(to), y: cy(to), h: to.height }, endRot, 1,
        () => retireOnBoard(el, gen, () => setHandHidden(destKey, false)),
        () => { el.remove(); setHandHidden(destKey, false); });
      return;
    }
    // into a pile (GY / banish / deck / extra): the card lands SQUARELY on the stack (owner-facing,
    // like the pile's printed top) and holds until the snapshot re-tops the pile beneath it — a real
    // arrival, not a fade-away that a new top card later blinks in for.
    const pileRot = m.to.controller !== duel.seat ? 180 : 0;
    fly({ x: cx(to), y: cy(to), h: to.height }, pileRot, 1, () => retireOnBoard(el, gen), () => el.remove());
  }

  // ── Xyz material attach/detach (travel with m.overlay; locations pre-normalized to the HOST zone).
  //    Never a full placement: NO dest-hide (the host Xyz monster stays visible throughout) and NO
  //    retireOnBoard (the material lives UNDER the host; the overlay badge is the persistent truth).
  //    Attach = the material tucks beneath the host (shrinks + fades out on arrival); detach = it
  //    slides out from under the host, growing back to size as it flies to the pile. ────────────────
  function overlayTravel(m: Extract<DuelMotion, { kind: "travel" }>): void {
    const attach = (m.to.location & FIELD) !== 0;
    if (attach) {
      const fromHand = (m.from.location & LOCATION.HAND) !== 0;
      const src = fromHand ? handSource(m.from.controller, m.code) : null;
      const fromRect = src?.rect ?? (fromHand ? null : (rectFor(m.from)?.rect ?? null));
      const to = rectFor(m.to)?.rect ?? null;
      if (!fromRect || !to) return;
      hideFieldSource(m.from); // the material DOES leave its zone; only the destination stays live
      src?.hide();
      const { el } = makeCard(fromRect, artFor(m.code, m.faceDown));
      const dx = cx(to) - cx(fromRect), dy = cy(to) - cy(fromRect);
      flyPath(el, {
        dx, dy, scEnd: 0.55 * (to.height / fromRect.height),
        rotEnd: m.to.controller !== duel.seat ? 180 : 0,
        lift: Math.min(60, Math.hypot(dx, dy) * 0.16) + 8, dur: DUR.travel, landOpacity: 0,
      }).then(() => el.remove(), () => el.remove());
      return;
    }
    // detach: emerge from beneath the host zone, small and dim, and grow out on the way to the pile.
    const fromRect = rectFor(m.from)?.rect ?? null;
    const to = rectFor(m.to)?.rect ?? null;
    if (!fromRect || !to) return;
    const { el } = makeCard(fromRect, artFor(m.code, m.faceDown));
    gsap.set(el, { scale: 0.55, opacity: 0 });
    const dx = cx(to) - cx(fromRect), dy = cy(to) - cy(fromRect);
    flyPath(el, {
      dx, dy, scStart: 0.55, scEnd: Math.max(0.32, Math.min(1.6, to.height / fromRect.height)),
      rotEnd: m.to.controller !== duel.seat ? 180 : 0,
      lift: Math.min(60, Math.hypot(dx, dy) * 0.16) + 8, dur: DUR.travel, fadeIn: true,
    }).then(() => el.remove(), () => el.remove());
  }

  // ── draw: each card peels off the deck and slides into the END of the hand — exactly where it will
  //    sit once the hand re-centres (a natural draw, like the games), not floating into the middle. ──
  function draw(m: Extract<DuelMotion, { kind: "draw" }>): void {
    const gen = boardGen;
    const deck = rectFor({ controller: m.controller, location: LOCATION.DECK, sequence: 0 });
    if (!deck) return;
    const side = m.controller === duel.seat ? "me" : "opp";
    const n0 = duel.board?.players[m.controller]?.hand.length ?? 0;
    const codes = m.cards.slice(0, 8);
    const n = codes.length;
    const dur = DUR.travel + 60;
    for (let i = 0; i < n; i++) {
      const slot = handSlot(m.controller, i, n);
      if (!slot) return;
      // the real card mounts HIDDEN under the flying copy when the snapshot lands; the copy holds its
      // exact slot until then, so the handoff is a same-paint swap, not a fade-out/pop-in.
      const hk = `hand:${side}:${n0 + i}`;
      setHandHidden(hk, true);
      // every draw peels off the deck as a BACK (the deck is face-down); your own turn over mid-arc —
      // the "look at the card" beat — the opponent's stay hidden all the way into their hand.
      const { el, face } = makeCard(deck.rect, artFor(codes[i]!, true));
      el.style.opacity = "0";
      if (codes[i]) faceFlip(face, artFor(codes[i]!, false), i * 120 + ANT_MS + dur * 0.3);
      flyPath(el, {
        dx: slot.x - cx(deck.rect), dy: slot.y - cy(deck.rect),
        scEnd: Math.max(0.6, Math.min(1.2, slot.h / deck.rect.height)),
        rotEnd: 0, lift: 34 + Math.abs(slot.x - cx(deck.rect)) * 0.08,
        dur, delay: i * 120, fadeIn: true,
      }).then(
        () => retireOnBoard(el, gen, () => setHandHidden(hk, false)),
        () => { el.remove(); setHandHidden(hk, false); });
    }
  }

  /** The on-screen centre of where the i-th of n freshly-drawn cards will sit in the hand once it
   *  re-centres. The hand is centre-justified, so its centre stays fixed and cards lay out around it at
   *  a fixed pitch (measured from the live cards); the drawn cards take the new right-most indices. */
  function handSlot(controller: number, i: number, n: number): { x: number; y: number; h: number } | null {
    const side = controller === duel.seat ? "me" : "opp";
    const cont = host?.ownerDocument.querySelector<HTMLElement>(`[data-mref-hand="${side}"]`)?.getBoundingClientRect();
    if (!cont) return null;
    const cards = [...(host.ownerDocument.querySelector(`[data-mref-hand="${side}"]`)?.children ?? [])]
      .map((c) => c.getBoundingClientRect()).filter((r) => r.width);
    const cw = cards[0]?.width ?? cont.height * 0.6;
    const pitch = cards.length >= 2 ? cards[1]!.left - cards[0]!.left : cw * 1.06;
    const total = cards.length + n;
    const k = cards.length + i;                       // this drawn card's index in the post-draw hand
    const centerX = cont.left + cont.width / 2;        // centre-justified → fixed regardless of count
    const y = cards[0] ? cards[0].top + cards[0].height / 2 : cont.top + cont.height / 2;
    return { x: centerX + (k - (total - 1) / 2) * pitch, y, h: cards[0]?.height ?? cont.height * 0.85 };
  }

  // ── destroy: the card DE-REZZES — it dissolves into a grid of data cells that wash out in a top-down
  //    scanline, each lifting and drifting as light toward the destination pile (graveyard / banished),
  //    not rubble falling under gravity. Cyberspace deletes; it doesn't shatter. ──────────────────────
  function dissolve(m: Extract<DuelMotion, { kind: "destroy" }>): void {
    const from = rectFor({ controller: m.controller, location: m.location, sequence: m.sequence });
    const to = rectFor(m.to)?.rect ?? null;
    if (!from) return;
    hideFieldSource({ controller: m.controller, location: m.location, sequence: m.sequence });
    const R = from.rect, img = artFor(m.code, false);
    flashAt(R, m.battle ? "#ff7ab0" : FX.magenta); // magenta data-corruption flash, not a fire-orange burst
    scanWipe(R, m.battle ? "#bff0ff" : FX.magenta); // a bright line sweeps down the card as it de-rezzes
    const COLS = 5, ROWS = 7; // finer grid → a "pixels / voxels" read, not chunks of glass
    const H = R.height, cellW = R.width / COLS, cellH = R.height / ROWS;
    const pile = to ? { x: cx(to), y: cy(to) } : { x: cx(R), y: R.top - H * 0.4 }; // no pile → rise off
    const SWEEP = 240; // ms over which the top-down dissolve wipe completes
    for (let row = 0; row < ROWS; row++) for (let col = 0; col < COLS; col++) {
      const el = document.createElement("div");
      el.className = "mfly-cell";
      const ox = R.left + (col + 0.5) * cellW, oy = R.top + (row + 0.5) * cellH;
      el.style.cssText = `left:${ox - cellW / 2}px;top:${oy - cellH / 2}px;width:${cellW}px;height:${cellH}px;background-image:url("${img}");background-size:${R.width}px ${R.height}px;background-position:-${col * cellW}px -${row * cellH}px`;
      host.appendChild(el);
      // Each cell is gently PULLED toward the pile (absorbed into the network) with a slight upward lift
      // and a small outward jitter — light dissolving, never a gravity arc.
      const tx = (pile.x - ox) * (0.45 + Math.random() * 0.2);
      const ty = (pile.y - oy) * (0.45 + Math.random() * 0.2) - H * (0.12 + Math.random() * 0.14);
      const jx = (Math.random() - 0.5) * cellW * 1.4;
      const rot = (Math.random() - 0.5) * 16;
      const delay = (row / ROWS) * SWEEP + Math.random() * 40; // top rows wash out first → a clean wipe
      const rm = () => el.remove();
      track(gsap.to(el, {
        keyframes: {
          "18%": { x: jx, y: -H * 0.06, scale: 1.04, opacity: 1 },
          "100%": { x: tx + jx * 0.4, y: ty, scale: 0.18, rotation: rot, opacity: 0 },
          easeEach: "none",
        },
        duration: DUR.hold / 1000, delay: delay / 1000, ease: OUT_QUINT,
        onComplete: rm, onInterrupt: rm,
      }));
    }
  }

  // ── a bright thin line that sweeps DOWN a card's bounds (the "de-rez scan"), then fades. ──────────
  function scanWipe(rect: ScreenRect, color: string): void {
    const el = document.createElement("div");
    el.className = "mfly-scan";
    const barH = Math.max(3, rect.height * 0.14);
    el.style.cssText = `left:${rect.left}px;top:${rect.top}px;width:${rect.width}px;height:${barH}px;color:${color};opacity:0`;
    host.appendChild(el);
    const rm = () => el.remove();
    track(gsap.to(el, {
      keyframes: {
        "0%": { y: -barH, opacity: 0 },
        "25%": { y: 0, opacity: 1 },
        "100%": { y: rect.height, opacity: 0 },
        easeEach: "none",
      },
      duration: (DUR.base + 80) / 1000, ease: OUT_EXPO, onComplete: rm, onInterrupt: rm,
    }));
  }

  // ── 2D ambient accents (the WebGL arena does these itself in 3D, so gate on a DOM-resolved zone) ──
  function flashAt(rect: ScreenRect, color: string): void {
    const el = document.createElement("div");
    el.className = "mfly-flash";
    const s = Math.max(rect.width, rect.height) * 1.5;
    el.style.cssText = `left:${cx(rect) - s / 2}px;top:${cy(rect) - s / 2}px;width:${s}px;height:${s}px;color:${color}`;
    host.appendChild(el);
    const rm = () => el.remove();
    track(gsap.fromTo(el, { scale: 0.4, opacity: 0.85 }, { scale: 1.15, opacity: 0, duration: DUR.travel / 1000, ease: OUT_EXPO, onComplete: rm, onInterrupt: rm }));
  }

  function accent(r: Ref, color: string): void {
    const f = rectFor(r);
    if (f?.dom) flashAt(f.rect, color); // DOM-resolved → flat 2D board; in 3D the arena flashes itself
  }

  function hideFieldSource(r: Ref): void {
    if ((r.location & FIELD) === 0) return; // only field cards leave a ghost worth hiding
    const key = mrefKey(r.controller, r.location, r.sequence);
    setRefHidden(key, true);
    setTimeout(() => setRefHidden(key, false), 1200); // safety net (board snapshot normally clears first)
  }

  // ── landing weight: a soft additive radial pulse at a zone — a pulse, not a firework (the 2D
  //    board's contract is intent, not spectacle; the ceremony budget lives in the 3D arena). ────────
  function glowPulse(rect: ScreenRect, color: string, o: { xyz?: boolean; size?: number } = {}): void {
    const el = document.createElement("div");
    el.className = "mfly-glow";
    if (o.xyz) el.classList.add("mfly-glow-xyz"); // the Xyz flavour: a white rim on a void-dark core
    const s = Math.max(rect.width, rect.height) * (o.size ?? 1.6);
    el.style.cssText = `left:${cx(rect) - s / 2}px;top:${cy(rect) - s / 2}px;width:${s}px;height:${s}px;color:${color}`;
    host.appendChild(el);
    const rm = () => el.remove();
    track(gsap.fromTo(el, { scale: 0.55, opacity: 0.9 }, { scale: 1.12, opacity: 0, duration: (DUR.base + 120) / 1000, ease: OUT_EXPO, onComplete: rm, onInterrupt: rm }));
  }

  // Extra-deck summon tints for the 2D landing pulse (the full type-colored ceremony is 3D-only).
  // Xyz is a special TREATMENT (white rim on dark), not a colour — see .mfly-glow-xyz.
  const EXTRA_TINT: Record<string, string> = { fusion: "#b96bff", synchro: "#eaf8ff", xyz: "#eaf8ff", link: FX.cyan, ritual: "#2c49ff" };

  // ── place: the landing weight at the destination zone. A token / engine-conjured card (no preceding
  //    MOVE flight — nothing travelled here) MATERIALISES: a copy grows in from the glow and holds the
  //    zone until the snapshot dresses the real card (same retire handoff as a flight landing). ──────
  function place2d(m: Extract<DuelMotion, { kind: "place" }>): void {
    const f = rectFor(m);
    if (!f?.dom) return;
    const color = m.extraType ? EXTRA_TINT[m.extraType]! : m.summon === "set" ? FX.setBlue : m.summon === "special" ? FX.lime : FX.cyan;
    // flip summons have no `from` either but the card is ALREADY on the board turning over — only a
    // normal/special arrival with no travel is a conjured card that needs to appear from somewhere.
    if ((m.summon === "normal" || m.summon === "special") && !m.from) {
      const gen = boardGen;
      const destKey = mrefKey(m.controller, m.location, m.sequence);
      const rot = (m.controller !== duel.seat ? 180 : 0) + ((m.location & LOCATION.MZONE) !== 0 && m.defense ? -90 : 0);
      const { el } = makeCard(f.rect, artFor(m.code, false));
      setHandHidden(destKey, true); // the snapshot may already show the token; the copy owns the zone until retire
      gsap.set(el, { scale: 0.4, opacity: 0, rotation: rot });
      const land = () => retireOnBoard(el, gen, () => setHandHidden(destKey, false));
      const fail = () => { el.remove(); setHandHidden(destKey, false); };
      track(gsap.to(el, { scale: 1, opacity: 1, duration: DUR.base / 1000, ease: OUT_QUINT, onComplete: land, onInterrupt: fail }));
    }
    glowPulse(f.rect, color, { xyz: m.extraType === "xyz" });
  }

  // ── attack: intent = WHO is attacking WHAT. After the declaration arc has held through responses,
  //    the attacker SLINGS BACK against the attack vector, then accelerates 72% into the defender and
  //    recoils home. That anticipation beat is the source of momentum (Master Duel's strongest read),
  //    while the defender's directional knock makes the impact unambiguous. ─────────────────────────
  function attackLunge(m: Extract<DuelMotion, { kind: "attack" }>): void {
    const el = innerCardEl(m.attacker);
    const from = el?.getBoundingClientRect();
    if (!el || !from?.width) return;
    let target: ScreenRect | null = null;
    let frac = 0.7;
    if (m.target) target = rectFor(m.target)?.rect ?? null;
    else {
      const row = host?.ownerDocument.querySelector<HTMLElement>(`[data-mref-hand="${m.attacker.controller === duel.seat ? "opp" : "me"}"]`);
      const cards = row ? [...row.children].map((el) => el.getBoundingClientRect()).filter((b) => b.width > 0 && b.height > 0) : [];
      const b = cards.length ? cards[Math.floor(cards.length / 2)]! : row?.getBoundingClientRect();
      if (b?.width) { target = b; frac = 0.6; } // direct attacks resolve through the defender's hand fan
    }
    if (!target) return;
    const dx = (cx(target) - cx(from)) * frac;
    const dy = (cy(target) - cy(from)) * frac;
    const pullX = -dx * 0.2;
    const pullY = -dy * 0.2;
    boardTween(el, (tl) => {
      // Pull back, HOLD to build tension, then take a longer accelerating run at the defender.
      // The contact frame is shared with the defender recoil + screen impact below.
      tl.to(el, { x: pullX, y: pullY, scale: 0.92, duration: ATTACK_TIMING.pullback / 1000, ease: OUT_QUINT })
        .to(el, { x: pullX, y: pullY, scale: 0.92, duration: ATTACK_TIMING.windup / 1000, ease: "none" })
        .to(el, { x: dx, y: dy, scale: 1.08, duration: ATTACK_TIMING.lunge / 1000, ease: IN_QUINT })
        .to(el, { x: 0, y: 0, scale: 1, duration: ATTACK_TIMING.recoil / 1000, ease: OUT_QUINT });
    });
    const defEl = m.target ? innerCardEl(m.target) : null;
    if (defEl) {
      const d = Math.hypot(dx, dy) || 1;
      boardTween(defEl, (tl) => {
        tl.to(defEl, { x: (dx / d) * 10, y: (dy / d) * 10, duration: 0.1, ease: OUT_EXPO }) // knocked along the blow
          .to(defEl, { x: 0, y: 0, duration: 0.22, ease: OUT_QUINT });
      }, ATTACK_TIMING.impact); // impact = the turnaround of the lunge
    }
  }

  // ── resolve: chain link N is firing RIGHT NOW — one bright pulse on its card so the player sees
  //    WHICH effect is resolving. Pile activations (a GY effect) pulse the pile's rect instead (the
  //    levitating hover card floats there already). ───────────────────────────────────────────────────
  function resolvePulse(m: Extract<DuelMotion, { kind: "resolve" }>): void {
    const ref = { controller: m.controller, location: m.location, sequence: m.sequence };
    const el = innerCardEl(ref);
    const rect = el?.getBoundingClientRect() ?? rectFor(ref)?.rect ?? null;
    if (!rect?.width) return;
    glowPulse(rect, "#bff0ff", { size: 1.35 }); // the bright cyan-white "this is firing" read
    if (el) boardTween(el, (tl) => {
      tl.to(el, { scale: 1.06, duration: DUR.quick / 1000, ease: OUT_QUINT })
        .to(el, { scale: 1, duration: (DUR.base - DUR.quick) / 1000, ease: OUT_QUINT });
    });
  }

  // ── shuffleHand: the hand gathers into a stack at the row centre and re-deals — it tells the player
  //    their knowledge of that hand's ORDER is now void. ─────────────────────────────────────────────
  function shuffleHandFx(m: Extract<DuelMotion, { kind: "shuffleHand" }>): void {
    const row = host?.ownerDocument.querySelector<HTMLElement>(`[data-mref-hand="${m.controller === duel.seat ? "me" : "opp"}"]`);
    const rowRect = row?.getBoundingClientRect();
    if (!row || !rowRect?.width) return;
    const centerX = rowRect.left + rowRect.width / 2;
    [...row.children].forEach((child, i) => {
      const card = child.querySelector<HTMLElement>(".tp-card");
      const r = child.getBoundingClientRect();
      if (!card || !r.width) return;
      boardTween(card, (tl) => {
        tl.to(card, { x: centerX - (r.left + r.width / 2), duration: 0.16, ease: OUT_QUINT })
          .to(card, { x: 0, duration: 0.18, ease: OUT_QUINT }, ">+0.04"); // a beat in the stack, then re-deal
      }, i * 22);
    });
  }

  // ── shuffleDeck: a quick riffle wiggle on the pile — post-search shuffles are constant, and total
  //    silence there reads as a missed beat. ─────────────────────────────────────────────────────────
  function shuffleDeckFx(m: Extract<DuelMotion, { kind: "shuffleDeck" }>): void {
    const stack = host?.ownerDocument.querySelector<HTMLElement>(`[data-mref="${mrefKey(m.controller, LOCATION.DECK, 0)}"]`);
    if (!stack || !stack.getBoundingClientRect().width) return;
    // The stable ref is the pile stack itself. Wiggle only its visible top card; its depth/slices keep
    // their physical pose and the ref rect stays reliable for any concurrent flight.
    const inner = stack.querySelector<HTMLElement>(".pile-top") ?? stack;
    if (!inner) return;
    boardTween(inner, (tl) => {
      tl.to(inner, { keyframes: { x: [0, 3, -3, 2, 0], easeEach: "power1.inOut" }, duration: 0.3 });
    });
  }

  // ── shuffleSet: the three-card monte — the face-down cards in that row visibly trade places (it's
  //    gameplay-relevant misdirection). Ends at identity; the board snapshot re-dresses the truth. ────
  function shuffleSetFx(m: Extract<DuelMotion, { kind: "shuffleSet" }>): void {
    const p = duel.board?.players[m.controller];
    const arr = (m.location & LOCATION.MZONE) ? p?.mzone : p?.szone;
    if (!arr) return;
    const spots: { el: HTMLElement; x: number }[] = [];
    arr.forEach((c, seq) => {
      if (!c || ((c.position ?? 0) & POS.FACEDOWN) === 0) return;
      const el = innerCardEl({ controller: m.controller, location: m.location, sequence: seq });
      const r = el?.getBoundingClientRect();
      if (el && r?.width) spots.push({ el, x: r.left + r.width / 2 });
    });
    if (spots.length < 2) return; // one card can't monte
    spots.forEach((s, i) => {
      const other = spots[(i + 1) % spots.length]!; // each card slides onto its neighbour's spot and back
      boardTween(s.el, (tl) => {
        tl.to(s.el, { x: other.x - s.x, scale: 1.04, duration: 0.2, ease: OUT_QUINT })
          .to(s.el, { x: 0, scale: 1, duration: 0.2, ease: OUT_QUINT }, ">+0.05");
      }, i * 15);
    });
  }

  // ── equip: the attach must be SEEN — a gold energy mote streaks equip→host along a small arc and
  //    a gold ring lands on the host (gold = the equip/link accent, matching the link chips). ────────
  function equipFx(m: Extract<DuelMotion, { kind: "equip" }>): void {
    const from = rectFor(m.from);
    const to = rectFor(m.to);
    if (!from?.dom || !to?.dom) return;
    const dot = document.createElement("div");
    dot.className = "mfly-dot";
    const s = 14;
    dot.style.cssText = `left:${cx(from.rect) - s / 2}px;top:${cy(from.rect) - s / 2}px;width:${s}px;height:${s}px;opacity:0`;
    host.appendChild(dot);
    const dx = cx(to.rect) - cx(from.rect), dy = cy(to.rect) - cy(from.rect);
    const lift = Math.min(46, Math.hypot(dx, dy) * 0.22) + 10;
    const rmDot = () => dot.remove();
    const tl = gsap.timeline({ onComplete: rmDot, onInterrupt: rmDot });
    tl.fromTo(dot, { scale: 0.4, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.09, ease: OUT_QUINT }, 0)
      .to(dot, { motionPath: { path: [{ x: 0, y: 0 }, { x: dx * 0.5, y: dy * 0.5 - lift }, { x: dx, y: dy }], curviness: 1.2 }, duration: DUR.travel / 1000, ease: OUT_QUINT }, 0)
      .to(dot, { opacity: 0, scale: 0.5, duration: 0.12, ease: OUT_QUINT }, ">-0.08"); // fades INTO the host
    track(tl);
    const ring = document.createElement("div");
    ring.className = "mfly-ring";
    const rs = Math.max(to.rect.width, to.rect.height) * 0.95;
    ring.style.cssText = `left:${cx(to.rect) - rs / 2}px;top:${cy(to.rect) - rs / 2}px;width:${rs}px;height:${rs}px;opacity:0`;
    host.appendChild(ring);
    const rmRing = () => ring.remove();
    track(gsap.fromTo(ring, { scale: 0.7, opacity: 0.9 }, {
      scale: 1.15, opacity: 0, duration: DUR.base / 1000, ease: OUT_EXPO,
      delay: (DUR.travel - 60) / 1000, onComplete: rmRing, onInterrupt: rmRing, // lands as the mote arrives
      immediateRender: false, // else the from-pose paints DURING the delay — a ring before the mote lands
    }));
  }

  onMount(() => {
    const off = onMotion((m: DuelMotion) => {
      if (reduced()) return; // reduced motion: cards just update in place (no flight, no shards)
      // In 3D the WebGL arena flies every card IN-SCENE (real 3D arcs off the hand fan / deck / piles)
      // and owns all the intent beats; this layer only keeps the screen-space de-rez dissolve there.
      // Flat 2D board: everything flies + pulses + lunges here.
      const in3d = arenaActive();
      switch (m.kind) {
        case "travel": if (!in3d) { if (m.overlay) overlayTravel(m); else travel(m); } break;
        case "draw": if (!in3d) draw(m); break;
        case "destroy": dissolve(m); break;
        // neon landing weight: normal = cyan (structure), special = lime (rare), set = dim cyan-blue,
        // extra-deck = the type tint. Gold is reserved for DP/equips — never a summon. Activation = magenta surge.
        case "place": if (!in3d) place2d(m); break;
        case "activate": accent(m, FX.magenta); break;
        case "attack": if (!in3d) attackLunge(m); break;
        case "resolve": if (!in3d) resolvePulse(m); break;
        case "shuffleHand": if (!in3d) shuffleHandFx(m); break;
        case "shuffleDeck": if (!in3d) shuffleDeckFx(m); break;
        case "shuffleSet": if (!in3d) shuffleSetFx(m); break;
        case "equip": if (!in3d) equipFx(m); break;
        default: break; // flip is owned by the renderers; lethal by the cinematics layer
      }
    });
    return () => {
      off();
      // kill everything we created: each onInterrupt removes its copy / restores its board card.
      for (const a of [...live]) a.kill();
      live.clear();
    };
  });
</script>

<div bind:this={host} class="mfly-host" aria-hidden="true"></div>

<style>
  /* A viewport-fixed, click-through overlay above the board but below modals/prompts. It must NOT sit
     under a transformed ancestor (transform re-bases fixed positioning), so it mounts outside the
     scaled duel stage. */
  .mfly-host {
    position: fixed;
    inset: 0;
    z-index: 45;
    pointer-events: none;
    overflow: hidden;
  }
  /* A flying card: the OUTER wrapper is owned by the path timeline (position / scale / rotation /
     the height-cue drop-shadow); the INNER face carries the art + chamfer and is owned by the flip
     timeline, so a card can turn over while it flies — two axes, no fighting transforms. */
  :global(.mfly-card) {
    position: fixed;
    perspective: 480px; /* the inner face's rotationY flip projects through this */
    /* STATIC shadow: an animated drop-shadow forced a repaint every frame, which let a board-apply's
       main-thread work freeze the card mid-arc. A constant one reads fine and stays composited. */
    filter: drop-shadow(0 10px 12px rgba(0, 0, 0, 0.38));
    will-change: transform, opacity;
  }
  :global(.mfly-cardface) {
    position: absolute;
    inset: 0;
    /* solid dark base UNDER the art: a 404'd image (custom cards) shows this instead of a washed
       white/transparent card — the 2D twin of the arena's NO ART face */
    background-color: #0d1526;
    background-size: cover;
    background-position: center;
    /* rounded rectangle, matching DuelCard — the chamfer cut is a ZONE shape, not a card shape */
    border-radius: 5px;
    box-shadow: 0 0 0 1px color-mix(in oklab, var(--color-cyan) 30%, transparent);
    will-change: transform;
  }
  /* One de-rez cell: a slice of the card art (background-position set inline) that dissolves to light.
     A faint cyan inner-glow + additive-ish edge sells "data", not a chunk of broken glass. */
  :global(.mfly-cell) {
    position: fixed;
    background-repeat: no-repeat;
    box-shadow: inset 0 0 0 0.5px color-mix(in oklab, var(--color-cyan) 35%, transparent),
                0 0 6px color-mix(in oklab, var(--color-magenta) 30%, transparent);
    will-change: transform, opacity;
  }
  /* The de-rez scanline: a bright thin bar (height set inline) that sweeps down the card as it dissolves. */
  :global(.mfly-scan) {
    position: fixed;
    background: linear-gradient(to bottom, transparent, color-mix(in oklab, currentColor 75%, white) 50%, transparent);
    box-shadow: 0 0 14px currentColor;
    mix-blend-mode: screen;
    will-change: transform, opacity;
  }
  /* A quick neon impact ring (flat-board ambient accent; the WebGL arena flashes itself in 3D). */
  :global(.mfly-flash) {
    position: fixed;
    border-radius: 50%;
    background: radial-gradient(circle, color-mix(in oklab, currentColor 55%, transparent) 0%, transparent 68%);
    will-change: transform, opacity;
  }
  /* Landing-weight pulse: a soft additive radial glow at a destination zone. Screen-blended so it
     reads as light ON the mat, not a painted disc. */
  :global(.mfly-glow) {
    position: fixed;
    border-radius: 50%;
    background: radial-gradient(circle,
      color-mix(in oklab, currentColor 60%, transparent) 0%,
      color-mix(in oklab, currentColor 22%, transparent) 40%,
      transparent 68%);
    mix-blend-mode: screen;
    will-change: transform, opacity;
  }
  /* The Xyz summon treatment: a white rim around a void-dark core — the "black hole with a white
     rim" read, so it must NOT screen-blend (screen can only lighten; the dark core would vanish). */
  :global(.mfly-glow-xyz) {
    background: radial-gradient(circle,
      color-mix(in oklab, var(--color-void, #05070d) 85%, transparent) 0% 38%,
      rgba(255, 255, 255, 0.92) 48%,
      transparent 62%);
    mix-blend-mode: normal;
  }
  /* The equip mote: a small gold energy dot that streaks equip→host. Gold is the equip/link accent
     (chain badges, link chips) — the one warm colour with an established meaning on this board. */
  :global(.mfly-dot) {
    position: fixed;
    border-radius: 50%;
    background: radial-gradient(circle, #fff3c4 0%, #ffd76a 45%, transparent 72%);
    box-shadow: 0 0 12px #ffd76a, 0 0 26px color-mix(in oklab, #ffd76a 55%, transparent);
    will-change: transform, opacity;
  }
  /* The equip arrival ring on the host card. */
  :global(.mfly-ring) {
    position: fixed;
    border-radius: 50%;
    border: 2px solid #ffd76a;
    box-shadow: 0 0 10px color-mix(in oklab, #ffd76a 60%, transparent),
                inset 0 0 8px color-mix(in oklab, #ffd76a 35%, transparent);
    will-change: transform, opacity;
  }
  @media (prefers-reduced-motion: reduce) {
    .mfly-host { display: none; }
  }
</style>
